// AnnouncementHistoryPanel.jsx
import React, { useMemo } from "react";
import { Drawer, Empty, Typography, Skeleton } from "antd";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import dayjs from "dayjs";
import "./AnnouncementHistoryPanel.css";
import { db } from "../../../services/firebaseService/connection";
import useGlobalStore from "../../../store/store";
import useUserStore, { selectUserDetails, selectUserId } from "../../../store/user/user";
import {
  BellOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  RightOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const AnnouncementHistoryPanel = ({ visible, onClose }) => {
  const userId = useUserStore(selectUserId);
  const user = useUserStore(selectUserDetails);

  const setCurrentAlert = useGlobalStore(
    (state) => state.alerts.setCurrentAlert,
  );
  const setPopupVisible = useGlobalStore(
    (state) => state.alerts.setPopupVisible,
  );

  // Get user's group IDs
  const userGroupIds = user?.userRoles
    ? Object.keys(user.userRoles).filter(
        (groupId) => user.userRoles[groupId] === "admin",
      )
    : [];

  const fetchAdminAlertsHistory = async () => {
    const alertsRef = collection(db, "adminAlerts");
    const q = query(
      alertsRef,
      where("isActive", "==", true),
      orderBy("createdAt", "desc"),
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };

  const fetchUserViewedAlerts = async () => {
    if (!userId) return [];

    const viewedRef = collection(db, "adminAlertViews", userId, "viewedAlerts");
    const snapshot = await getDocs(viewedRef);

    return snapshot.docs.map((doc) => doc.id);
  };

  // Fetch all alerts
  const { data: allAlerts = [], isLoading: alertsLoading } = useQuery({
    queryKey: ["adminAlertsHistory"],
    queryFn: fetchAdminAlertsHistory,
    enabled: visible && !!userId,
  });

  // Fetch user's viewed alerts
  const { data: viewedAlertIds = [], isLoading: viewedLoading } = useQuery({
    queryKey: ["userViewedAlertsPanel", userId],
    queryFn: fetchUserViewedAlerts,
    enabled: visible && !!userId,
  });

  // Filter alerts relevant to user
  const relevantAlerts = allAlerts.filter((alert) => {
    if (!alert.targetType || alert.targetType === "global") return true;

    if (alert.targetType === "group_specific") {
      return alert.targetGroupIds?.some((groupId) =>
        userGroupIds.includes(groupId),
      );
    }

    return false;
  });

  const isLoading = alertsLoading || viewedLoading;

  const unreadCount = useMemo(() => {
    if (isLoading) return 0;
    return (
      relevantAlerts.filter((alert) => !viewedAlertIds.includes(alert.id))
        ?.length || 0
    );
  }, [relevantAlerts, viewedAlertIds, isLoading]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return dayjs(date).format("MMM D, YYYY");
  };

  const truncateContent = (htmlContent, maxLength = 100) => {
    if (!htmlContent) return "";
    const textContent = htmlContent.replace(/<[^>]*>/g, "");
    if (textContent.length <= maxLength) return textContent;
    return textContent.substring(0, maxLength) + "...";
  };

  const handleCardClick = (alert) => {
    setCurrentAlert(alert);
    setPopupVisible(true);
    // Delay closing the panel so React can commit the popup state before
    // the Drawer starts its exit animation (avoids a race where the panel
    // closes but the popup never opens).
    setTimeout(onClose, 0);
  };

  return (
    <Drawer
      title={
        <div className="announcement-panel-header">
          <BellOutlined className="announcement-header-icon" />
          <span className="announcement-header-title">Alerts</span>
          {unreadCount > 0 && (
            <span className="announcement-unread-badge">{unreadCount} new</span>
          )}
          <CloseOutlined onClick={onClose} className="announcement-close-btn" />
        </div>
      }
      placement="right"
      closable={false}
      maskClosable={false}
      onClose={onClose}
      open={visible}
      width={420}
      className="announcement-history-panel"
      styles={{
        body: { padding: 0 },
      }}
    >
      {isLoading ? (
        <div className="announcement-skeleton-container">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="announcement-list-item"
              style={{ cursor: "default" }}
            >
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : relevantAlerts.length === 0 ? (
        <div className="announcement-empty-container">
          <Empty
            description={
              <span style={{ color: "#8c8c8c" }}>No alerts yet</span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="announcement-list-container">
          {relevantAlerts.map((alert) => {
            const isUnread = !viewedAlertIds.includes(alert.id);

            return (
              <div
                key={alert.id}
                className={`announcement-list-item ${isUnread ? "unread" : ""}`}
                onClick={() => handleCardClick(alert)}
              >
                <div className="announcement-list-content">
                  <div className="announcement-list-header">
                    <Title level={5} className="announcement-list-title">
                      {alert.title}
                    </Title>
                    {isUnread && (
                      <div className="announcement-active-indicator" />
                    )}
                  </div>

                  <Text className="announcement-list-preview">
                    {truncateContent(alert.description || alert.message)}
                  </Text>

                  <div className="announcement-list-footer">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <ClockCircleOutlined className="announcement-footer-icon" />
                      <Text className="announcement-list-date">
                        {formatDate(alert.createdAt)}
                      </Text>
                    </div>
                  </div>
                </div>
                <RightOutlined className="announcement-list-arrow" />
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};

export default AnnouncementHistoryPanel;
